import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Lock } from 'lucide-react';
import { useState } from 'react';

import { OrgCard } from '@/features/OrgLayout/primitives/OrgCard';
import { StudentWorkTab } from '@/features/Trainer/StudentWorkTab';
import { WorkArtifactSheet } from '@/features/Trainer/WorkArtifactSheet';
import { Skeleton } from '@/components/ui/skeleton';
import { trainerApi, type StudentWork, type WorkItem } from '@/lib/api/trainer';

/**
 * Everything this student has been set and done — the trainer's own feed, read-only.
 *
 * Replaces a "Recent Activity" card that listed six graded assessments with a
 * percentage each. That card could not answer the question a TPO actually
 * arrives with: not "what did they score" but "what have they been doing, and
 * did they do it themselves". It showed no practice problems, nothing that was
 * never started, and nothing about how the work was produced.
 *
 * The rows are the trainer's component unchanged. Two renderings of the same
 * evidence drift, and a college reading one story while their trainer acts on
 * another is precisely the failure this feature exists to prevent.
 *
 * Clicking a row opens *this student's own solution*. From a person's page the
 * work they did is the natural destination; the whole-batch view stays one
 * click further, at the bottom of the sheet.
 */
export function StudentWorkCard({
  batchId,
  userId,
  studentName,
  batchStudentCount,
}: {
  batchId: string;
  userId: string;
  studentName: string;
  batchStudentCount?: number;
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState<WorkItem | null>(null);

  const { data, isLoading, isError } = useQuery<StudentWork>({
    queryKey: ['studentWork', batchId, userId],
    queryFn: async () => (await trainerApi.getStudentWork(batchId, userId)).data,
  });

  return (
    <OrgCard className="p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-lg font-semibold text-[#111827]">Work</h3>
        <span className="inline-flex items-center gap-1.5 text-xs text-[#9ca3af]">
          <Lock className="h-3.5 w-3.5" aria-hidden="true" />
          Read-only — the same feed the trainer works from
        </span>
      </div>

      <div className="mt-4">
        {isLoading && (
          <div className="space-y-2" aria-busy="true">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-14 w-full rounded-lg" />
            ))}
          </div>
        )}
        {isError && (
          <p className="py-8 text-center text-sm text-[#9ca3af]">
            This student&apos;s work could not be loaded.
          </p>
        )}
        {data && (
          <StudentWorkTab
            work={data}
            onOpenItem={setOpen}
            openLabel={`open what ${studentName} submitted`}
          />
        )}
      </div>

      <WorkArtifactSheet
        batchId={batchId}
        assessmentId={open?.id ?? ''}
        student={open ? { id: userId, name: studentName } : null}
        onClose={() => setOpen(null)}
        batchStudentCount={batchStudentCount}
        // Offered only for work the course set. There is no "how everyone did"
        // for a problem the student found on their own.
        onSeeEveryone={
          open && open.source !== 'self'
            ? () => {
                const assessmentId = open.id;
                setOpen(null);
                void navigate({
                  to: '/org/batches/$batchId/work-items/$assessmentId',
                  params: { batchId, assessmentId },
                });
              }
            : undefined
        }
      />
    </OrgCard>
  );
}
